import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { AudioCaptureIndicator } from "@/components/layout/components/audio-capture";
import { VoiceActions } from "@/components/note/floating-actions/voice-actions";
import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { MDSIconKind, MDSPopover } from "@/mds";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";
import { useVoiceIndicatorBehavior } from "@/components/note/editor/top-bar/indicators/voice-indicator/voice-indicator.hooks";
import { AudioRecordingObservable } from "@/store/audio-recordings";
import { TerminalOutcome } from "@/store/audio/pipeline/audio-pipeline.types";
import { AnyNoteVoiceSuggestion } from "@/store/note";
import styled from "@emotion/styled";
import { useAppStore } from "@/store";
import { getShortcutTooltipConfig } from "@/modules/keyboard-shortcuts/tooltip";

interface VoiceIndicatorProps {
  audioRecording?: AudioRecordingObservable | null;
  noteSuggestion?: AnyNoteVoiceSuggestion | null;
  editorStore: MemCommonEditorStore;
  isActiveRecording?: boolean;
  toggleRequestCount?: number;
  durationLabelOverride?: string | null;
}

export const VoiceIndicator = observer(
  ({
    audioRecording,
    noteSuggestion,
    editorStore,
    isActiveRecording,
    toggleRequestCount = 0,
    durationLabelOverride,
  }: VoiceIndicatorProps) => {
    const { store } = useAppStore();
    const {
      handleRequestClose,
      ref,
      setIsOpen,
      isOpen,
      popoverContentRef,
      isOutsideCloseSuppressed,
    } = useVoiceIndicatorBehavior({
      noteId: editorStore.id,
      audioRecording,
      isSuggestionActive: Boolean(editorStore.noteObservable?.isSuggestionActive),
    });

    const handleClick = () => {
      setIsOpen(isOpen => !isOpen);
    };

    useEffect(() => {
      if (!toggleRequestCount) {
        return;
      }

      setIsOpen(isOpen => !isOpen);
    }, [setIsOpen, toggleRequestCount]);

    // Handle click outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const targetNode = event.target as Node;
        const isClickInsidePopover = popoverContentRef.current?.contains(targetNode);
        const isClickOnTrigger = ref.current?.contains(targetNode);

        const targetElement = targetNode as Element;
        const isClickOnAudioCaptureDropdown = targetElement.closest(
          "[data-action-menu-with-content]"
        );
        const isClickInsideModal = targetElement.closest("[data-mds-name='modal-dialog']");

        if (
          !isClickInsidePopover &&
          !isClickOnAudioCaptureDropdown &&
          !isClickOnTrigger &&
          !isClickInsideModal
        ) {
          if (isOutsideCloseSuppressed()) return;
          handleRequestClose();
        }
      };

      // Watch for resize handle drag state changes using MutationObserver.
      // Because mousedown is not captured when resizing the split view.
      const observer = new MutationObserver(() => {
        // Check if any resize handle is in drag state
        const draggingHandle = document.querySelector('[data-resize-handle-state="drag"]');
        if (draggingHandle) {
          handleRequestClose();
        }
      });

      // Observe document body for attribute changes on resize handles
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ["data-resize-handle-state"],
        subtree: true,
      });

      // Use capture phase to catch events before they bubble
      document.addEventListener("mousedown", handleClickOutside, { capture: true });

      return () => {
        document.removeEventListener("mousedown", handleClickOutside, { capture: true });
        observer.disconnect();
      };
    }, [isOpen, handleRequestClose, popoverContentRef, ref, isOutsideCloseSuppressed]);

    // Handle window resize
    useEffect(() => {
      window.addEventListener("resize", handleRequestClose);
      return () => window.removeEventListener("resize", handleRequestClose);
    }, [isOpen, handleRequestClose]);

    const hasTransformPipelineError = (() => {
      if (!audioRecording) return false;
      const snapshot = store.audio.pipeline.getSnapshot(audioRecording.id);
      if (!snapshot || snapshot.terminalOutcome === null) return false;
      return (
        snapshot.terminalOutcome !== TerminalOutcome.Success &&
        snapshot.terminalOutcome !== TerminalOutcome.NoUsableAudioFound
      );
    })();

    return (
      <>
        <MDSTooltip
          config={getShortcutTooltipConfig({
            action: "toggle_voice_cleanup_panel",
            label: "Toggle Voice panel",
            placement: MDSTooltipPlacement.Top,
          })}
        >
          <AudioCaptureIndicator
            ref={ref}
            insideNote={true}
            onClick={handleClick}
            rightIconKind={MDSIconKind.AngleDown}
            audioRecording={audioRecording}
            noteSuggestion={noteSuggestion}
            isActiveRecording={isActiveRecording}
            isErrorState={hasTransformPipelineError}
            dataVoiceIndicatorTrigger={true}
            durationLabelOverride={durationLabelOverride}
          />
        </MDSTooltip>
        <MDSPopover
          styling={false}
          triggerRef={ref}
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          isNonModal={true}
          shouldCloseOnInteractOutside={element => {
            if (isOutsideCloseSuppressed()) return false;
            const target = element as Element | null;
            if (!target) return true;
            if (target.closest("[data-voice-indicator-trigger]")) return false;
            if (target.closest("[data-action-menu-with-content]")) return false;
            if (target.closest("[data-mds-name='modal-dialog']")) return false;
            return true;
          }}
        >
          <VoiceActionsContainer data-voice-actions-root ref={popoverContentRef} tabIndex={-1}>
            <VoiceActions editorStore={editorStore} onRequestClose={handleRequestClose} />
          </VoiceActionsContainer>
        </MDSPopover>
      </>
    );
  }
);

const VoiceActionsContainer = styled.div`
  outline: none;
  /*
   * Prevent lateral shifts when changing from AudioCaptureActions to CleanUpActions.
   */
  width: 450px;
`;
